/**
 * VehicleCheckBadges.tsx — 車両チェックの「掃 / 洗 / M」バッジ（2026-08-09）
 *
 * 従来の5つの丸ランプ（車内VC・車外VC・清掃・洗車・その他）を、現場で読み取りやすい
 * 3つの四角バッジに置き換えたもの。
 *
 *   掃 = 清掃（指定カラー: 点検マスタの bg_color。既定 緑 #10B981）
 *   洗 = 洗車（同上。既定 青 #3B82F6）
 *   M  = その他の必須チェック全部（ワイパー/ランプ類/オイル/タイヤ等）
 *        マスタに指定カラーが無いため紫 #7c3aed を使う
 *
 * 各バッジは3状態:
 *   未完了            … 薄いグレーの文字＋枠
 *   完了              … 指定カラーの文字＋枠（背景は白）
 *   ビデオチェック完了 … 背景が指定カラー・文字は白
 *     ※ 掃 は「車内VC」、洗 は「車外VC」と対応する（2026-08-09 確認）。
 *        M に対応する VC は存在しないため、M は 未完了/完了 の2状態。
 *        M の完了は白抜きではなく**紫の塗り＋白文字**にする（2026-09-20 前田さん指示。
 *        3状態を持つ 掃/洗 と違い、M は完了＝最終状態なので塗りで揃える）
 */
import React from 'react';
import { Box, Tooltip } from '@mui/material';

export interface CheckSituation {
  id: number;
  name?: string;
  require_flg?: boolean;
  value?: boolean;
  bg_color?: string;
}

/** 「掃」「洗」に対応する作業チェックとビデオチェックの組 */
const BADGE_DEFS = [
  { key: '掃', checkName: '清掃', vcName: '車内VC', fallbackColor: '#10B981', label: '清掃' },
  { key: '洗', checkName: '洗車', vcName: '車外VC', fallbackColor: '#3B82F6', label: '洗車' },
] as const;

/** 「その他」= 掃/洗/VC 以外の必須チェック。指定カラーが無いので紫 */
const OTHER_COLOR = '#7c3aed';
const NAMED_CHECK_NAMES = new Set(['清掃', '洗車', '車内VC', '車外VC']);

type BadgeState = 'none' | 'done' | 'video';

interface Props {
  /** 車両の check_situations（require_flg / value / name / bg_color を含む） */
  checkSituations: CheckSituation[] | undefined;
  /** バッジの文字サイズ(rem)。既定 0.55 */
  fontSize?: number;
  /** バッジ1辺の長さ(px)。正方形で表示する。既定 15 */
  size?: number;
}

export default function VehicleCheckBadges({ checkSituations, fontSize = 0.55, size = 15 }: Props) {
  const situations = Array.isArray(checkSituations) ? checkSituations : [];
  const required = situations.filter(c => c.require_flg === true);
  if (required.length === 0) return null;

  const findByName = (name: string) => required.find(c => c.name === name);
  const isDone = (c: CheckSituation | undefined) => c?.value === true;

  const badges: { key: string; color: string; state: BadgeState; tooltip: string }[] = [];

  BADGE_DEFS.forEach(def => {
    const work = findByName(def.checkName);
    const vc = findByName(def.vcName);
    // マスタに項目自体が無い店舗ではバッジを出さない（無言で「未完了」を出さない）
    if (!work) return;
    const color = work.bg_color || def.fallbackColor;
    const workDone = isDone(work);
    const vcDone = !!vc && isDone(vc);
    const state: BadgeState = workDone && vcDone ? 'video' : workDone ? 'done' : 'none';
    const tooltip = `${def.label}: ${workDone ? '完了' : '未完了'}`
      + (vc ? ` / ${def.vcName}: ${vcDone ? '完了' : '未完了'}` : '');
    badges.push({ key: def.key, color, state, tooltip });
  });

  // M: その他の必須チェック（1件も無ければ出さない）
  const others = required.filter(c => !NAMED_CHECK_NAMES.has(String(c.name)));
  if (others.length > 0) {
    const incomplete = others.filter(c => !isDone(c)).map(c => c.name);
    badges.push({
      key: 'M',
      color: OTHER_COLOR,
      // M は完了＝最終状態なので 'video'（塗り）で表す
      state: incomplete.length === 0 ? 'video' : 'none',
      tooltip: incomplete.length === 0
        ? `その他チェック(${others.length}件): 全て完了`
        : `その他チェック 未完了: ${incomplete.join(' / ')}`,
    });
  }

  if (badges.length === 0) return null;

  return (
    <Box sx={{ display: 'flex', gap: '3px', alignItems: 'center' }}>
      {badges.map(b => {
        const filled = b.state === 'video';
        const active = b.state !== 'none';
        return (
          <Tooltip key={b.key} title={b.tooltip} arrow>
            <Box component="span" sx={{
              // 正方形で表示する（2026-08-09）
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: `${size}px`, height: `${size}px`,
              boxSizing: 'border-box', flexShrink: 0,
              fontSize: `${fontSize}rem`,
              fontWeight: 800,
              lineHeight: 1,
              borderRadius: '2px',
              border: `1px solid ${active ? b.color : '#d4d4d8'}`,
              backgroundColor: filled ? b.color : '#fff',
              color: filled ? '#fff' : active ? b.color : '#c7c7cc',
            }}>{b.key}</Box>
          </Tooltip>
        );
      })}
    </Box>
  );
}
